import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import './ProductivityStats.css';

interface ProductivityData {
  period: string;
  dateRange: {
    start: string | null;
    end: string | null;
  };
  summary: {
    totalActions: number;
    totalActionsToday: number;
    totalActionsThisWeek: number;
    totalActionsThisMonth: number;
    activeUsers: number;
    dailyAverage: number;
  };
  audits: {
    total: number;
    today: number;
    thisWeek: number;
    thisMonth: number;
    byUser: { [key: string]: number };
    byUserDetailed: { [key: string]: any };
    byDay: any[];
    dailyAverage: number;
  };
  emailTasks: {
    total: number;
    today: number;
    thisWeek: number;
    thisMonth: number;
    pending: number;
    inProgress: number;
    byUser: { [key: string]: number };
    byUserToday: { [key: string]: number };
    byUserThisWeek: { [key: string]: number };
    byUserThisMonth: { [key: string]: number };
    byDay: { [key: string]: number };
    dailyAverage: number;
    byType: { [key: string]: number };
  };
  idVerifications?: {
    summary: {
      total: number;
      today: number;
      thisWeek: number;
      thisMonth: number;
    };
    byUser: { [key: string]: any };
  };
  userProductivity: {
    [key: string]: {
      audits: {
        total: number;
        today: number;
        thisWeek: number;
        thisMonth: number;
      };
      emailTasks: {
        total: number;
        today: number;
        thisWeek: number;
        thisMonth: number;
      };
      idVerifications?: {
        total: number;
        today: number;
        thisWeek: number;
        thisMonth: number;
      };
      combined: {
        total: number;
        today: number;
        thisWeek: number;
        thisMonth: number;
      };
    };
  };
  userNames: { [key: string]: string };
}

interface TimeSeriesData {
  date: string;
  audits: number;
  tasks: number;
  total: number;
}

export const ProductivityStats: React.FC = () => {
  const [data, setData] = useState<ProductivityData | null>(null);
  const [timeSeries, setTimeSeries] = useState<TimeSeriesData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<'today' | 'week' | 'month' | 'all'>('week');
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());
  
  const { apiRequest } = useAuth();

  const fetchProductivityData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch productivity analytics (period-based) and all-time user data separately
      const [productivityData, allTimeUserData, timeSeriesData] = await Promise.all([
        apiRequest<ProductivityData>({
          url: `/ui/admin/productivity/analytics?period=${selectedPeriod}`,
          method: 'GET'
        }),
        apiRequest<ProductivityData>({
          url: `/ui/admin/productivity/analytics?period=all`,
          method: 'GET'
        }),
        apiRequest<{ timeSeries: TimeSeriesData[] }>({
          url: `/ui/admin/productivity/time-series?days=30&metric=combined`,
          method: 'GET'
        })
      ]);
      
      // Use period-based data for summary cards but all-time data for user table
      const combinedData = {
        ...productivityData,
        userProductivity: allTimeUserData.userProductivity,
        userNames: allTimeUserData.userNames
      };
      
      setData(combinedData);
      setTimeSeries(timeSeriesData.timeSeries);
      
      // Debug logging
      console.log('Productivity data:', combinedData);
      console.log('User productivity count:', Object.keys(combinedData.userProductivity || {}).length);
    } catch (err: any) {
      console.error('Error fetching productivity data:', err);
      setError(err.message || 'Failed to load productivity data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProductivityData();
  }, [selectedPeriod]);

  const toggleCardExpansion = (cardId: string) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(cardId)) {
      newExpanded.delete(cardId);
    } else {
      newExpanded.add(cardId);
    }
    setExpandedCards(newExpanded);
  };


  const formatNumber = (num: number): string => {
    return num.toLocaleString();
  };

  const formatPeriodLabel = (period: string): string => {
    switch (period) {
      case 'today': return 'Today';
      case 'week': return 'This Week';
      case 'month': return 'This Month';
      case 'all': return 'All Time';
      default: return period;
    }
  };

  if (loading) {
    return (
      <div className="productivity-stats-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="productivity-stats-error">
        <p>Error: {error}</p>
        <button onClick={fetchProductivityData} className="retry-btn">
          Retry
        </button>
      </div>
    );
  }

  if (!data) {
    return <div>No data available</div>;
  }

  return (
    <div className="productivity-stats">
      {/* Period Selector */}
      <div className="period-selector">
        <div className="period-tabs">
          {(['today', 'week', 'month', 'all'] as const).map((period) => (
            <button
              key={period}
              className={`period-tab ${selectedPeriod === period ? 'active' : ''}`}
              onClick={() => setSelectedPeriod(period)}
            >
              {formatPeriodLabel(period)}
            </button>
          ))}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="stats-cards-grid">
        {/* Total Actions Card */}
        <div 
          className={`stat-card expandable ${expandedCards.has('total') ? 'expanded' : ''}`}
          onClick={() => toggleCardExpansion('total')}
        >
          <div className="stat-card-header">
            <h3>Total Actions</h3>
            <span className="expand-icon">{expandedCards.has('total') ? '▼' : '▶'}</span>
          </div>
          <div className="stat-value">{formatNumber(data.summary.totalActions)}</div>
          <div className="stat-subtitle">
            Daily Average: {data.summary.dailyAverage}
          </div>
          
          {expandedCards.has('total') && (
            <div className="stat-breakdown">
              <div className="breakdown-item">
                <span>Audits:</span>
                <span>
                  {formatNumber(data.audits.total)}
                </span>
              </div>
              <div className="breakdown-item">
                <span>Email Tasks:</span>
                <span>
                  {formatNumber(data.emailTasks.total)}
                </span>
              </div>
              <div className="breakdown-item">
                <span>ID Verifications:</span>
                <span>
                  {formatNumber(data.idVerifications?.summary?.total || 0)}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Today's Actions Card */}
        <div className="stat-card">
          <div className="stat-card-header">
            <h3>Today</h3>
          </div>
          <div className="stat-value">{formatNumber(data.summary.totalActionsToday)}</div>
          <div className="stat-subtitle">
            <span>Audits: {data.audits.today}</span>
            <span className="separator">•</span>
            <span>Tasks: {data.emailTasks.today}</span>
            <span className="separator">•</span>
            <span>IDs: {data.idVerifications?.summary?.today || 0}</span>
          </div>
        </div>

        {/* This Week Card */}
        <div className="stat-card">
          <div className="stat-card-header">
            <h3>This Week</h3>
          </div>
          <div className="stat-value">{formatNumber(data.summary.totalActionsThisWeek)}</div>
          <div className="stat-subtitle">
            <span>Audits: {data.audits.thisWeek}</span>
            <span className="separator">•</span>
            <span>Tasks: {data.emailTasks.thisWeek}</span>
            <span className="separator">•</span>
            <span>IDs: {data.idVerifications?.summary?.thisWeek || 0}</span>
          </div>
        </div>

        {/* This Month Card */}
        <div className="stat-card">
          <div className="stat-card-header">
            <h3>This Month</h3>
          </div>
          <div className="stat-value">{formatNumber(data.summary.totalActionsThisMonth)}</div>
          <div className="stat-subtitle">
            <span>Audits: {data.audits.thisMonth}</span>
            <span className="separator">•</span>
            <span>Tasks: {data.emailTasks.thisMonth}</span>
            <span className="separator">•</span>
            <span>IDs: {data.idVerifications?.summary?.thisMonth || 0}</span>
          </div>
        </div>

        {/* Active Users Card */}
        <div className="stat-card">
          <div className="stat-card-header">
            <h3>Active Users</h3>
          </div>
          <div className="stat-value">{data.summary.activeUsers}</div>
          <div className="stat-subtitle">Contributing team members</div>
        </div>

        {/* Email Tasks Status Card */}
        <div 
          className={`stat-card expandable ${expandedCards.has('email-status') ? 'expanded' : ''}`}
          onClick={() => toggleCardExpansion('email-status')}
        >
          <div className="stat-card-header">
            <h3>Email Task Status</h3>
            <span className="expand-icon">{expandedCards.has('email-status') ? '▼' : '▶'}</span>
          </div>
          <div className="stat-value">{data.emailTasks.pending + data.emailTasks.inProgress}</div>
          <div className="stat-subtitle">Pending & In Progress</div>
          
          {expandedCards.has('email-status') && (
            <div className="stat-breakdown">
              <div className="breakdown-item">
                <span>Pending:</span>
                <span>{data.emailTasks.pending}</span>
              </div>
              <div className="breakdown-item">
                <span>In Progress:</span>
                <span>{data.emailTasks.inProgress}</span>
              </div>
              <div className="breakdown-item">
                <span>Completed:</span>
                <span>{data.emailTasks.total}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Enhanced User Productivity Table */}
      <div className="user-productivity-section">
        <h3>User Productivity Details (All Time)</h3>
        <div className="productivity-table-wrapper">
          <table className="user-productivity-table">
            <thead>
              <tr>
                <th>User</th>
                <th colSpan={4} className="category-header">Audits</th>
                <th colSpan={4} className="category-header">Email Tasks</th>
                <th colSpan={4} className="category-header">ID Verifications</th>
                <th colSpan={4} className="category-header">Combined Total</th>
              </tr>
              <tr>
                <th></th>
                <th>Today</th>
                <th>Week</th>
                <th>Month</th>
                <th>All</th>
                <th>Today</th>
                <th>Week</th>
                <th>Month</th>
                <th>All</th>
                <th>Today</th>
                <th>Week</th>
                <th>Month</th>
                <th>All</th>
                <th>Today</th>
                <th>Week</th>
                <th>Month</th>
                <th>All</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.userProductivity)
                .sort(([, a], [, b]) => b.combined.total - a.combined.total)
                .map(([email, stats]) => (
                  <tr key={email} className="user-row">
                    <td className="user-email">{data.userNames?.[email] || email}</td>
                    <td className="stat-cell">{stats.audits.today}</td>
                    <td className="stat-cell">{stats.audits.thisWeek}</td>
                    <td className="stat-cell">{stats.audits.thisMonth}</td>
                    <td className="stat-cell">{stats.audits.total}</td>
                    <td className="stat-cell">{stats.emailTasks.today}</td>
                    <td className="stat-cell">{stats.emailTasks.thisWeek}</td>
                    <td className="stat-cell">{stats.emailTasks.thisMonth}</td>
                    <td className="stat-cell">{stats.emailTasks.total}</td>
                    <td className="stat-cell">{stats.idVerifications?.today || 0}</td>
                    <td className="stat-cell">{stats.idVerifications?.thisWeek || 0}</td>
                    <td className="stat-cell">{stats.idVerifications?.thisMonth || 0}</td>
                    <td className="stat-cell">{stats.idVerifications?.total || 0}</td>
                    <td className="stat-cell combined-total">{stats.combined.today}</td>
                    <td className="stat-cell combined-total">{stats.combined.thisWeek}</td>
                    <td className="stat-cell combined-total">{stats.combined.thisMonth}</td>
                    <td className="stat-cell combined-total">{stats.combined.total}</td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Time Series Chart (Simple) */}
      <div className="time-series-section">
        <h3>Activity Trend (Last 30 Days)</h3>
        <div className="simple-chart">
          {timeSeries.map((day, index) => {
            const maxValue = Math.max(...timeSeries.map(d => d.total), 1);
            const height = (day.total / maxValue) * 100;
            
            return (
              <div key={day.date} className="chart-bar-container">
                <div 
                  className="chart-bar"
                  style={{ height: `${height}%` }}
                  title={`${day.date}: ${day.total} total (${day.audits} audits, ${day.tasks} tasks)`}
                >
                  <div 
                    className="bar-segment audits"
                    style={{ height: `${(day.audits / day.total) * 100}%` }}
                  />
                  <div 
                    className="bar-segment tasks"
                    style={{ height: `${(day.tasks / day.total) * 100}%` }}
                  />
                </div>
                {index % 5 === 0 && (
                  <div className="chart-label">
                    {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="chart-legend">
          <span className="legend-item">
            <span className="legend-color audits"></span> Audits
          </span>
          <span className="legend-item">
            <span className="legend-color tasks"></span> Email Tasks
          </span>
        </div>
      </div>

    </div>
  );
};